import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useSectionMotion, useMagnetico } from './motion';
import Frase from './Frase';
import { useIdioma, type Traducir } from '../../i18n/idioma';

/* Claves, no texto: el arreglo vive en el módulo, fuera del hook. */
const PASOS = ['lp.pasos.1', 'lp.pasos.2', 'lp.pasos.3', 'lp.pasos.4'] as const;

interface PasosProps {
    onPrimary: () => void;
    signedIn: boolean;
}

/**
 * Los cuatro pasos para arrancar.
 *
 * Aquí los números 01–04 SÍ se ganan su lugar: esto es una secuencia real y el
 * orden es información que el lector necesita. (Numerar cada sección de una
 * página, en cambio, es el andamiaje de IA que se eliminó del resto.)
 *
 * Tampoco son cuatro tarjetas idénticas: es un riel único que hilvana los
 * cuatro momentos y se dibuja conforme bajas — horizontal en escritorio,
 * vertical en el teléfono.
 */
const Pasos: React.FC<PasosProps> = ({ onPrimary, signedIn }) => {
    const { t } = useIdioma();
    const boton = useMagnetico<HTMLButtonElement>();

    const scope = useSectionMotion<HTMLElement>(({ gsap, scope, reveal, full, titular }) => {
        const rejilla = scope.querySelector('[data-rejilla]');

        titular({ start: 'top 76%' });

        /* El riel se dibuja de izquierda a derecha (o de arriba abajo en el
           teléfono). Corre en los dos niveles de movimiento: es una línea de
           1 px que crece, no un elemento que atraviese la pantalla. */
        gsap.from(scope.querySelectorAll('[data-riel]'), {
            scaleX: 0,
            scaleY: 0,
            duration: full ? 1.7 : 0.95,
            ease: 'power2.inOut',
            scrollTrigger: { trigger: rejilla, start: 'top 74%' },
        });

        reveal('[data-paso]', { trigger: rejilla, start: 'top 72%', y: 28, stagger: 0.13 });
        reveal('[data-fade]', { start: 'top 72%', stagger: 0.09 });
    });

    return (
        <section
            ref={scope}
            id="empezar"
            className="rp-on-light bg-white px-5 py-[clamp(5rem,11vw,9rem)] sm:px-8"
        >
            <div className="mx-auto max-w-[1240px]">
                {/* El tope de medida va SOBRE el <h2>, nunca sobre un div que lo
                    envuelva: `ch` se calcula contra el tamaño de letra del propio
                    elemento, así que 24ch en un contenedor de 16 px son ~190 px y
                    parten el titular en cinco renglones con una «a» sola. */}
                <h2 className="rp-h2 max-w-[15ch] text-navy-950">
                    <Frase marca="titular">{t('lp.pasos.titular')}</Frase>
                </h2>
                <p data-fade className="rp-lead mt-6 text-acero-700">
                    {t('lp.pasos.lead')}
                </p>

                <div data-rejilla className="relative mt-[clamp(3.5rem,7vw,5.5rem)]">
                    {/* Riel horizontal (escritorio). Se ancla al centro de la fila
                        de números y se dibuja de izquierda a derecha. */}
                    <span
                        aria-hidden="true"
                        className="absolute left-0 right-0 top-[5px] hidden h-px bg-acero-200 lg:block"
                    />
                    <span
                        data-riel
                        aria-hidden="true"
                        className="absolute left-0 right-0 top-[5px] hidden h-px origin-left bg-navy-850 lg:block"
                    />
                    {/* Riel vertical (teléfono). */}
                    <span
                        aria-hidden="true"
                        className="absolute bottom-8 left-[5px] top-2 w-px bg-acero-200 lg:hidden"
                    />
                    <span
                        data-riel
                        aria-hidden="true"
                        className="absolute bottom-8 left-[5px] top-2 w-px origin-top bg-navy-850 lg:hidden"
                    />

                    <ol className="grid gap-y-12 lg:grid-cols-4 lg:gap-x-8">
                        {PASOS.map((clave, i) => (
                            <li
                                key={clave}
                                data-paso
                                className="relative pl-8 lg:pl-0 lg:pr-6"
                            >
                                {/* Punto del riel. El primero va en amarillo: es
                                    dónde empieza el lector. */}
                                <span
                                    aria-hidden="true"
                                    className={`absolute left-0 top-2 h-[11px] w-[11px] rounded-full border-2 border-navy-850 lg:left-auto lg:top-0 ${
                                        i === 0 ? 'bg-brand-400' : 'bg-white'
                                    }`}
                                />
                                {/* Ya no hay «PASO 01» en versalitas rastreadas.
                                    Cuatro rótulos así, en fila, son el mismo tic
                                    de mayúsculas chiquitas que se eliminó del
                                    resto de la página — y aquí sobraban: el riel
                                    con sus cuatro puntos ya dice que esto es una
                                    secuencia, y el orden de lectura hace el resto. */}
                                <h3 className="text-[1.0625rem] font-bold tracking-tight text-navy-950 lg:mt-8">
                                    {t(`${clave}.titulo` as Parameters<Traducir>[0])}
                                </h3>
                                <p className="mt-2 text-[15px] leading-relaxed text-acero-700">
                                    {t(`${clave}.texto` as Parameters<Traducir>[0])}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>

                <div data-fade className="mt-[clamp(3rem,6vw,4.5rem)]">
                    <button ref={boton} type="button" onClick={onPrimary} className="rp-btn rp-btn--primary">
                        {signedIn ? t('lp.cta.dashboard') : t('lp.cta.crearCuenta')}
                        <ArrowRight className="h-[18px] w-[18px]" aria-hidden="true" />
                    </button>
                    {/* Los oficios bajaron aquí desde la portada. Arriba eran uno
                        de los siete elementos que la abarrotaban; aquí, junto al
                        botón de crear cuenta, contestan la pregunta que toca en
                        ese momento exacto: «¿esto es para lo que yo hago?». */}
                    <p className="mt-6 max-w-[54ch] text-[15px] leading-relaxed text-acero-700">
                        {t('lp.pasos.oficios')}
                    </p>
                </div>
            </div>
        </section>
    );
};

export default Pasos;
